import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { BlogPostSEO } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { Calendar, Clock, ArrowLeft, ArrowRight, Loader2, Quote, BarChart2, ExternalLink, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { MarkdownRenderer } from '@/components/blog/MarkdownRenderer';
import { BlogCTASection } from '@/components/blog/BlogCTASection';
import { BlogSidebar } from '@/components/blog/BlogSidebar';
import { TableOfContents } from '@/components/blog/TableOfContents';
import { TopicClusterNav } from '@/components/blog/TopicClusterNav';
import { injectInternalLinks } from '@/lib/internal-links';
import { ReadingProgressBar } from '@/components/blog/ReadingProgressBar';
import { SocialShareButtons } from '@/components/blog/SocialShareButtons';
import { AuthorBox } from '@/components/blog/AuthorBox';
import { LeiaTambemMedicos } from '@/components/blog/LeiaTabemMedicos';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

interface ExpertQuote {
  quote: string;
  author: string;
  title: string;
  source_url?: string;
}

interface Statistic {
  value: string;
  description: string;
  source: string;
  source_url?: string;
  year?: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface BlogPostData {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  category: string;
  featured_image_url: string | null;
  status: string;
  published_at: string | null;
  meta_title: string | null;
  meta_description: string | null;
  meta_keywords: string[] | null;
  read_time_minutes: number | null;
  created_at: string;
  geo_score: number | null;
  expert_quotes: ExpertQuote[] | null;
  statistics: Statistic[] | null;
  authority_citations: string[] | null;
  faq_schema: { mainEntity: FAQItem[] } | null;
  freshness_date: string | null;
  cluster_id: string | null;
  is_pillar: boolean;
}

interface RelatedPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  category: string;
  read_time_minutes: number | null;
  featured_image_url: string | null;
}

interface AdjacentPost {
  title: string;
  slug: string;
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<RelatedPost[]>([]);
  const [prevPost, setPrevPost] = useState<AdjacentPost | null>(null);
  const [nextPost, setNextPost] = useState<AdjacentPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Check if slug has timestamp (old format) and redirect to clean URL
  const hasTimestamp = (slugToCheck: string): boolean => {
    return /\-\d{13,}$/.test(slugToCheck);
  };

  // Remove timestamp from slug to get clean version
  const removeTimestamp = (slugWithTimestamp: string): string => {
    return slugWithTimestamp.replace(/\-\d{13,}$/, '');
  };

  useEffect(() => {
    if (slug) {
      // 301 Redirect: If old URL with timestamp, redirect to clean URL
      if (hasTimestamp(slug)) {
        const cleanSlug = removeTimestamp(slug);
        // Use replace: true to simulate 301 behavior (replaces history entry)
        navigate(`/blog/${cleanSlug}`, { replace: true });
        return;
      }
      fetchPost(slug);
    }
  }, [slug, navigate]);

  const fetchPost = async (postSlug: string) => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', postSlug)
        .eq('status', 'published')
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        // Try to find post by partial slug match (for edge cases)
        const { data: partialMatch } = await supabase
          .from('blog_posts')
          .select('slug')
          .eq('status', 'published')
          .ilike('slug', `${postSlug}%`)
          .limit(1)
          .maybeSingle();

        if (partialMatch && partialMatch.slug !== postSlug) {
          // Redirect to the correct slug
          navigate(`/blog/${partialMatch.slug}`, { replace: true });
          return;
        }

        setNotFound(true);
        setLoading(false);
        return;
      }

      setPost(data as unknown as BlogPostData);

      // Increment view count (fire-and-forget)
      supabase.rpc('increment_blog_views', { post_slug: postSlug }).then();

      // Fetch related posts + prev/next in parallel
      const publishedAt = data.published_at || data.created_at;

      const [relatedRes, prevRes, nextRes] = await Promise.all([
        supabase
          .from('blog_posts')
          .select('id, title, slug, excerpt, category, read_time_minutes, featured_image_url')
          .eq('status', 'published')
          .eq('category', data.category)
          .neq('id', data.id)
          .order('views', { ascending: false })
          .limit(3),
        supabase
          .from('blog_posts')
          .select('title, slug')
          .eq('status', 'published')
          .eq('category', data.category)
          .lt('published_at', publishedAt)
          .order('published_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('blog_posts')
          .select('title, slug')
          .eq('status', 'published')
          .eq('category', data.category)
          .gt('published_at', publishedAt)
          .order('published_at', { ascending: true })
          .limit(1)
          .maybeSingle(),
      ]);

      setRelatedPosts((relatedRes.data as RelatedPost[]) || []);
      setPrevPost(prevRes.data as AdjacentPost | null);
      setNextPost(nextRes.data as AdjacentPost | null);
    } catch (error) {
      console.error('Error fetching post:', error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };




  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!post || notFound) {
    return (
      <div className="min-h-screen bg-background">
        <SEOHead
          title="Artigo não encontrado | Contabilidade Zen"
          description="Este artigo não está mais disponível. Confira outros conteúdos no blog da Contabilidade Zen."
          noindex={true}
          nofollow={true}
        />
        <Header />
        <main className="container mx-auto px-4 py-20 text-center max-w-2xl">
          <h1 className="text-3xl font-bold text-foreground mb-4">Artigo não encontrado</h1>
          <p className="text-muted-foreground mb-8">
            Este artigo não está mais disponível ou foi removido. Confira outros conteúdos no nosso blog.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild>
              <Link to="/blog">Ver todos os artigos</Link>
            </Button>
            <Button variant="outline" asChild>
              <a href="https://wa.me/5511999999999?text=Ol%C3%A1%21%20Vim%20pelo%20blog%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es." target="_blank" rel="noopener noreferrer">
                Falar no WhatsApp
              </a>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const expertQuotes = post.expert_quotes || [];
  const statistics = post.statistics || [];
  const authorityCitations = post.authority_citations || [];

  return (
    <div className="min-h-screen bg-background">
      <BlogPostSEO
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt || post.title}
        slug={post.slug}
        publishedTime={post.published_at || post.created_at}
        modifiedTime={post.freshness_date || post.published_at || post.created_at}
        featuredImage={post.featured_image_url || undefined}
        category={post.category}
        tags={post.meta_keywords || undefined}
        wordCount={post.content.split(/\s+/).length}
        readTimeMinutes={post.read_time_minutes || 5}
        lastModified={post.freshness_date || post.published_at || post.created_at}
        faqs={post.faq_schema?.mainEntity
          ?.filter((item: any) => (item.name || item.question) && (item.acceptedAnswer?.text || item.answer))
          .map((item: any) => ({
            question: String(item.name || item.question),
            answer: String(item.acceptedAnswer?.text || item.answer)
          }))
        }
      />

      <ReadingProgressBar />
      <Header />

      <main>
        {/* Breadcrumb */}
        <nav className="bg-muted/30 py-4" aria-label="Breadcrumb">
          <div className="container mx-auto px-4">
            <ol className="flex items-center gap-2 text-sm text-muted-foreground" itemScope itemType="https://schema.org/BreadcrumbList">
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/" className="hover:text-foreground transition-colors" itemProp="item">
                  <span itemProp="name">Início</span>
                </Link>
                <meta itemProp="position" content="1" />
              </li>
              <span>/</span>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/blog" className="hover:text-foreground transition-colors" itemProp="item">
                  <span itemProp="name">Blog</span>
                </Link>
                <meta itemProp="position" content="2" />
              </li>
              <span>/</span>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem" className="text-foreground truncate max-w-xs">
                <span itemProp="name">{post.title}</span>
                <meta itemProp="position" content="3" />
              </li>
            </ol>
          </div>
        </nav>

        {/* Article Header */}
        <header className="bg-gradient-hero py-12 lg:py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <Badge variant="secondary" className="text-sm">
                {post.category}
              </Badge>
              <span className="flex items-center gap-1 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                {post.read_time_minutes || 5} min de leitura
              </span>
              {post.geo_score && post.geo_score >= 80 && (
                <Badge variant="outline" className="gap-1 text-green-600 border-green-500/30">
                  <BarChart2 className="h-3 w-3" />
                  GEO {post.geo_score}
                </Badge>
              )}
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">
              {post.title}
            </h1>

            {post.excerpt && (
              <p className="text-lg text-muted-foreground mb-6">
                {post.excerpt}
              </p>
            )}

            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {format(new Date(post.published_at || post.created_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                </span>
                {post.freshness_date && (
                  <span className="flex items-center gap-2 text-primary">
                    <RefreshCw className="h-4 w-4" />
                    Atualizado em {format(new Date(post.freshness_date), "MMMM 'de' yyyy", { locale: ptBR })}
                  </span>
                )}
              </div>

              <SocialShareButtons 
                url={`https://www.contabilidadezen.com.br/blog/${post.slug}`} 
                title={post.title} 
              />
            </div>
          </div>
        </header>

        {/* Key Statistics Highlight (GEO-friendly) */}
        {statistics.length > 0 && (
          <section className="py-8 bg-primary/5 border-y border-primary/10">
            <div className="container mx-auto px-4 max-w-4xl">
              <h2 className="text-sm font-semibold text-primary mb-4 flex items-center gap-2">
                <BarChart2 className="h-4 w-4" />
                Dados em Destaque
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {statistics.slice(0, 3).map((stat, index) => (
                  <Card key={index} className="bg-background/50 border-primary/20">
                    <CardContent className="pt-4">
                      <p className="text-2xl font-bold text-primary">{stat.value}</p>
                      <p className="text-sm text-muted-foreground mt-1">{stat.description}</p>
                      <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                        <ExternalLink className="h-3 w-3" />
                        {stat.source} {stat.year && `(${stat.year})`}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Article Content */}
        <div className="py-12 lg:py-16">
          <div className="container mx-auto px-4 flex gap-8">
            <article className="flex-1 min-w-0 max-w-4xl">
            {/* Featured Image */}
            {post.featured_image_url && (
              <figure className="mb-8">
                <img
                  src={post.featured_image_url}
                  alt={post.title}
                  className="w-full rounded-xl shadow-md"
                  loading="eager"
                  decoding="async"
                />
              </figure>
            )}

            {/* Table of Contents */}
            <TableOfContents content={post.content} />

            {/* Topic Cluster Navigation */}
            <TopicClusterNav postId={post.id} clusterId={post.cluster_id} isPillar={post.is_pillar} />
            {/* Content with auto internal links */}
            <MarkdownRenderer content={injectInternalLinks(post.content)} />

            {/* "Leia também" for médicos category */}
            {(post.category.toLowerCase().includes('médico') || post.category.toLowerCase().includes('medico') || post.category.toLowerCase().includes('saúde') || post.category.toLowerCase().includes('saude')) && (
              <LeiaTambemMedicos />
            )}

            {/* Mid-Content CTA */}
            <BlogCTASection position="mid" postTitle={post.title} />

            {/* Expert Quotes Section (GEO-friendly) */}
            {expertQuotes.length > 0 && (
              <div className="mt-12 pt-8 border-t border-border">
                <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
                  <Quote className="h-5 w-5 text-primary" />
                  O que dizem os especialistas
                </h3>
                <div className="space-y-6">
                  {expertQuotes.map((quote, index) => (
                    <blockquote 
                      key={index}
                      className="border-l-4 border-primary pl-6 py-4 bg-muted/30 rounded-r-lg"
                      itemScope 
                      itemType="https://schema.org/Quotation"
                    >
                      <p 
                        className="text-lg italic text-foreground mb-3"
                        itemProp="text"
                      >
                        "{quote.quote}"
                      </p>
                      <footer className="flex items-center gap-2">
                        <cite 
                          className="text-sm font-medium text-foreground not-italic"
                          itemProp="author"
                          itemScope
                          itemType="https://schema.org/Person"
                        >
                          <span itemProp="name">{quote.author}</span>
                        </cite>
                        <span className="text-muted-foreground">—</span>
                        <span className="text-sm text-muted-foreground">{quote.title}</span>
                      </footer>
                    </blockquote>
                  ))}
                </div>
              </div>
            )}

            {/* Sources Section (GEO-friendly) */}
            {authorityCitations.length > 0 && (
              <div className="mt-12 pt-8 border-t border-border">
                <h3 className="text-sm font-semibold text-muted-foreground mb-4 flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Fontes e Referências
                </h3>
                <ul className="space-y-2 text-sm">
                  {authorityCitations.map((citation, index) => (
                    <li key={index} className="text-muted-foreground hover:text-foreground transition-colors">
                      <a 
                        href={citation} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-start gap-2"
                      >
                        <span className="text-primary">{index + 1}.</span>
                        <span className="break-all">{citation}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tags */}
            {post.meta_keywords && post.meta_keywords.length > 0 && (
              <div className="mt-12 pt-8 border-t border-border">
                <h3 className="text-sm font-semibold text-muted-foreground mb-3">Tags:</h3>
                <div className="flex flex-wrap gap-2">
                  {post.meta_keywords.map((keyword, index) => (
                    <Badge key={index} variant="outline" className="text-sm">
                      {keyword}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Visual FAQ Section */}
            {post.faq_schema?.mainEntity && post.faq_schema.mainEntity.length > 0 && (
              <div className="mt-12 pt-8 border-t border-border">
                <h2 className="text-xl font-bold text-foreground mb-6">Perguntas Frequentes</h2>
                <Accordion type="single" collapsible className="w-full">
                  {post.faq_schema.mainEntity.map((faq: any, index: number) => (
                    <AccordionItem key={index} value={`faq-${index}`}>
                      <AccordionTrigger className="text-left text-foreground hover:text-primary">
                        {faq.name || faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed">
                        {faq.acceptedAnswer?.text || faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}

            {/* Author Box (E-E-A-T) */}
            <AuthorBox />

            {/* Freshness Footer + Social Share */}
            <div className="mt-12 pt-8 border-t border-border">
              <div className="text-center mb-6">
                <p className="text-sm text-muted-foreground">
                  <strong>Última atualização:</strong>{' '}
                  {format(new Date(post.freshness_date || post.published_at || post.created_at), "MMMM 'de' yyyy", { locale: ptBR })}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Revisado por: <strong>Equipe Contabilidade Zen</strong>
                </p>
              </div>
              <div className="flex justify-center">
                <SocialShareButtons 
                  url={`https://www.contabilidadezen.com.br/blog/${post.slug}`} 
                  title={post.title} 
                />
              </div>
            </div>

            {/* End-Content CTA */}
            <BlogCTASection position="end" postTitle={post.title} />
            </article>

            <BlogSidebar />
          </div>
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="py-12 lg:py-16 bg-muted/30">
            <div className="container mx-auto px-4">
              <h2 className="text-2xl font-bold text-foreground mb-8">
                Artigos relacionados
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedPosts.map((related) => (
                  <article
                    key={related.id}
                    className="group bg-card rounded-2xl overflow-hidden border border-border hover:border-secondary/50 hover:shadow-card transition-all"
                  >
                    {related.featured_image_url ? (
                      <Link to={`/blog/${related.slug}`}>
                        <img
                          src={related.featured_image_url}
                          alt={related.title}
                          className="w-full h-40 object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                      </Link>
                    ) : (
                      <div className="h-2 bg-gradient-to-r from-zen-teal to-zen-blue" />
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                        <Badge variant="secondary" className="font-medium">
                          {related.category}
                        </Badge>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {related.read_time_minutes || 5} min
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold mb-3 text-foreground group-hover:text-secondary transition-colors line-clamp-2">
                        <Link to={`/blog/${related.slug}`}>
                          {related.title}
                        </Link>
                      </h3>
                      {related.excerpt && (
                        <p className="text-muted-foreground line-clamp-2 text-sm">
                          {related.excerpt}
                        </p>
                      )}
                      <Link
                        to={`/blog/${related.slug}`}
                        className="inline-flex items-center gap-1 text-secondary font-medium text-sm mt-4 hover:gap-2 transition-all"
                      >
                        Ler mais
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Prev/Next Navigation */}
        {(prevPost || nextPost) && (
          <nav className="py-8 bg-background border-t border-border" aria-label="Navegação entre artigos">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {prevPost ? (
                  <Link
                    to={`/blog/${prevPost.slug}`}
                    className="group flex items-center gap-3 p-4 rounded-xl border border-border hover:border-secondary/50 hover:shadow-card transition-all"
                  >
                    <ArrowLeft className="h-5 w-5 text-muted-foreground group-hover:text-secondary shrink-0" />
                    <div className="min-w-0">
                      <span className="text-xs text-muted-foreground">Artigo anterior</span>
                      <p className="text-sm font-semibold text-foreground group-hover:text-secondary transition-colors line-clamp-1">
                        {prevPost.title}
                      </p>
                    </div>
                  </Link>
                ) : <div />}
                {nextPost && (
                  <Link
                    to={`/blog/${nextPost.slug}`}
                    className="group flex items-center gap-3 p-4 rounded-xl border border-border hover:border-secondary/50 hover:shadow-card transition-all text-right md:justify-end"
                  >
                    <div className="min-w-0">
                      <span className="text-xs text-muted-foreground">Próximo artigo</span>
                      <p className="text-sm font-semibold text-foreground group-hover:text-secondary transition-colors line-clamp-1">
                        {nextPost.title}
                      </p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-secondary shrink-0" />
                  </Link>
                )}
              </div>
            </div>
          </nav>
        )}

        {/* Back to Blog */}
        <section className="py-8 bg-background">
          <div className="container mx-auto px-4">
            <Button variant="outline" asChild>
              <Link to="/blog" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Voltar para o Blog
              </Link>
            </Button>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 lg:py-20 bg-gradient-hero">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Quer saber mais sobre contabilidade para profissionais da saúde?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Entre em contato conosco e descubra como podemos ajudar você a pagar menos impostos de forma legal.
            </p>
            <Button variant="zen" size="lg" asChild>
              <Link to="/contato">
                Fale com um especialista
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
